import { useState, useEffect } from 'react';
import {
  Phone, Mail, MapPin, Clock, Send, Star,
  MessageCircle, Calendar, CheckCircle, ArrowRight,
  Instagram, Facebook, Heart,
  User, Home, Palette, Building, Leaf, Quote,
  Shield, Trophy, Zap, Users, ChevronLeft, X
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import emailjs from 'emailjs-com';

const ContactPage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service: '',
    otherService: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({ type: '', message: '' });
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll au haut de la page à chaque changement d'URL
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Intersection Observer pour les animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Fonction de sanitization pour prévenir XSS
  const sanitizeInput = (input) => {
    return input
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  };

  // Validation du téléphone tunisien (8 chiffres)
  const validatePhone = (phone) => {
    const phoneDigits = phone.replace(/\D/g, '');
    return phoneDigits.length === 8 && /^[0-9]{8}$/.test(phoneDigits);
  };

  // Validation de l'email
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Validation du nom (2-50 caractères, lettres et espaces)
  const validateName = (name) => {
    const nameRegex = /^[a-zA-ZÀ-ÿ\s]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  // Validation du sujet (3-100 caractères)
  const validateSubject = (subject) => {
    return subject.trim().length >= 3 && subject.trim().length <= 100;
  };

  // Validation du message (10-1000 caractères)
  const validateMessage = (message) => {
    return message.trim().length >= 1 && message.trim().length <= 1000;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Pour le téléphone, formatter automatiquement
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [name]: digits.slice(0, 8)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  // Afficher notification
  const showNotificationPopup = (type, message) => {
    setNotificationMessage({ type, message });
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    // Validation du nom
    if (!validateName(formData.name)) {
      errors.name = 'Le nom doit contenir entre 2 et 50 caractères (lettres uniquement)';
    }

    // Validation de l'email
    if (!validateEmail(formData.email)) {
      errors.email = 'Veuillez entrer une adresse email valide';
    }

    // Validation du téléphone si fourni
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Le numéro doit contenir exactement 8 chiffres';
    }

    // Validation du service
    if (!formData.service) {
      errors.service = 'Veuillez sélectionner un service';
    }

    // Validation du service "autre"
    if (formData.service === 'autre' && formData.otherService.trim().length < 3) {
      errors.otherService = 'Veuillez préciser le service souhaité (min 3 caractères)';
    }

    // Validation du sujet
    if (!validateSubject(formData.subject)) {
      errors.subject = 'Le sujet doit contenir entre 3 et 100 caractères';
    }

    // Validation du message
    if (!validateMessage(formData.message)) {
      errors.message = 'Le message doit contenir entre 10 et 1000 caractères';
    }

    // Si erreurs, les afficher
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showNotificationPopup('error', 'Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const userID = process.env.REACT_APP_EMAILJS_USER_ID;

      // Sanitize all inputs before sending
      const sanitizedData = {
        name: sanitizeInput(formData.name.trim()),
        email: sanitizeInput(formData.email.trim()),
        phone: formData.phone ? '+216 ' + formData.phone : 'Non renseigné',
        service: formData.service === 'autre'
          ? sanitizeInput(formData.otherService.trim())
          : services.find(s => s.value === formData.service)?.label || formData.service,
        message: sanitizeInput(formData.message.trim()),
        subject: sanitizeInput(formData.subject.trim()),
        year: new Date().getFullYear()
      };

      await emailjs.send(serviceID, templateID, sanitizedData, userID);

      showNotificationPopup('success', '✅ Message envoyé avec succès ! Nous vous répondrons sous 24h.');

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        service: '',
        otherService: '',
        message: ''
      });
    } catch (error) {
      console.error('Erreur:', error);
      showNotificationPopup('error', '❌ Erreur lors de l\'envoi. Veuillez réessayer ou nous contacter par téléphone.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Téléphone",
      details: ["+216 97 286 007"],
      color: "from-blue-500 to-cyan-500",
      action: "Appelez-nous"
    },
    {
      icon: Mail,
      title: "Email",
      details: ["placof.decor@gmail.com"],
      color: "from-amber-500 to-orange-500",
      action: "Écrivez-nous"
    },
    {
      icon: MapPin,
      title: "Adresse",
      details: ["Sfax, Tunisie"],
      color: "from-green-500 to-emerald-500",
      action: "Visitez-nous"
    },
    {
      icon: Clock,
      title: "Horaires",
      details: ["À votre écoute à tout moment"],
      color: "from-purple-500 to-pink-500",
      action: "Planifiez"
    }
  ];

  // Liste des services améliorée
  const services = [
    { value: "decoration", label: "Décoration Intérieure", icon: Home, desc: "Maisons, appartements, villas" },
    { value: "commercial", label: "Design Commercial", icon: Building, desc: "Bureaux, boutiques, restaurants" },
    { value: "exterieur", label: "Aménagement Extérieur", icon: Leaf, desc: "Jardins, terrasses, façades" },
    { value: "conseil", label: "Conseil Couleurs & Matériaux", icon: Palette, desc: "Palette, matériaux, harmonie" },
    { value: "renovation", label: "Rénovation & Transformation", icon: Zap, desc: "Espaces à rénover ou transformer" },
    { value: "autre", label: "Autre Projet (précisez)", icon: Star, desc: "Projet personnalisé" }
  ];

  const socialLinks = [
    {
      icon: Instagram,
      name: "Instagram",
      url: "https://www.instagram.com/placof/",
      color: "from-pink-500 to-purple-500"
    },
    {
      icon: Facebook,
      name: "Facebook",
      url: "https://www.facebook.com/placof",
      color: "from-blue-500 to-indigo-500"
    },
    {
      icon: "tiktok",
      name: "TikTok",
      url: "https://www.tiktok.com/@amir_decor",
      color: "from-black to-gray-800"
    }
  ];

  const testimonials = [
    {
      text: "Une équipe exceptionnelle ! Ils ont transformé notre salon selon nos rêves.",
      rating: 5,
      service: "Décoration Intérieure"
    },
    {
      text: "Professionnalisme et créativité au rendez-vous. Notre boutique est magnifique !",
      rating: 5,
      service: "Design Commercial"
    },
    {
      text: "Délais respectés, qualité irréprochable. Je recommande vivement !",
      rating: 5,
      service: "Aménagement Extérieur"
    }
  ];

  const whyChooseUs = [
    {
      icon: Trophy,
      title: "Excellence Reconnue",
      description: "Plus de 200 projets réalisés avec succès depuis 2012"
    },
    {
      icon: Users,
      title: "Équipe Experte",
      description: "Designers et artisans qualifiés à votre service"
    },
    {
      icon: Shield,
      title: "Garantie Qualité",
      description: "Suivi post-projet et garantie sur tous nos travaux"
    },
    {
      icon: Heart,
      title: "Satisfaction Client",
      description: "100% de nos clients nous recommandent"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contactez-Nous - Placof Décor</title>
        <meta
          name="description"
          content="Contactez Placof Décor pour un devis gratuit en décoration intérieure, faux plafonds et placoplâtre à Sfax. Nous répondons rapidement !"
        />
        <meta name="keywords" content="contact Placof Décor, devis faux plafond Sfax, entreprise décoration Tunisie" />

        {/* Open Graph */}
        <meta property="og:title" content="Contactez-Nous - Placof Décor | Devis Gratuit à Sfax" />
        <meta property="og:description" content="Obtenez un devis personnalisé pour vos projets de décoration intérieure." />

        <link rel="canonical" href={`https://placofdecor.tn${pathname}`} />

        <script type="application/ld+json">
          {`
              {
                "@context": "https://schema.org",
                "@type": "ContactPage",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+21697286007",
                  "contactType": "Customer Service"
                }
              }
            `}
        </script>
      </Helmet>

      {/* Popup de Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className={`bg-white rounded-2xl shadow-2xl border-2 p-4 min-w-[320px] max-w-md ${notificationMessage.type === 'success'
            ? 'border-green-500'
            : 'border-red-500'
            }`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${notificationMessage.type === 'success'
                ? 'bg-green-100'
                : 'bg-red-100'
                }`}>
                {notificationMessage.type === 'success' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <X className="w-6 h-6 text-red-600" />
                )}
              </div>
              <div className="flex-1">
                <h4 className={`font-bold mb-1 ${notificationMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                  {notificationMessage.type === 'success' ? 'Succès' : 'Erreur'}
                </h4>
                <p className="text-gray-700 text-sm">
                  {notificationMessage.message}
                </p>
              </div>
              <button
                onClick={() => setShowNotification(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 overflow-hidden">
        {/* Particules de fond */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full opacity-20 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.2}s`,
                transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.005}px)`
              }}
            />
          ))}
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto text-center relative z-10">
            <div
              className="space-y-8"
              style={{
                transform: `translateY(${scrollY * 0.1}px)`
              }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full border border-amber-200/50 shadow-lg">
                <MessageCircle className="w-5 h-5 text-amber-600 animate-pulse" />
                <span className="text-amber-700 font-semibold">Contactez-nous</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent leading-tight">
                Parlons de Votre
                <span className="block text-4xl sm:text-3xl lg:text-4xl mt-2">
                  Projet Déco
                </span>
              </h1>

              <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Nous sommes là pour transformer vos espaces en
                <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-semibold"> lieux d'exception</span>.
                Contactez-nous pour une consultation gratuite !
              </p>
            </div>
          </div>

          {/* Élément décoratif */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-full blur-3xl -z-10" />
        </section>

        {/* Informations de Contact */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Nos Coordonnées
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Plusieurs moyens de nous joindre pour votre convenience
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  id={`contact-info-${index}`}
                  data-animate
                  className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 text-center transform hover:scale-105 ${isVisible[`contact-info-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  style={{
                    transitionDelay: `${index * 0.1}s`
                  }}
                >
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${info.color} flex items-center justify-center group-hover:rotate-12 transition-transform duration-300`}>
                    <info.icon className="w-8 h-8 text-white group-hover:animate-bounce" />
                  </div>

                  <h3 className={`text-xl font-bold mb-4 bg-gradient-to-r ${info.color} bg-clip-text text-transparent`}>
                    {info.title}
                  </h3>

                  <div className="space-y-2 mb-6">
                    {info.details.map((detail, detailIndex) => (
                      <p key={detailIndex} className="text-gray-600">
                        {detail}
                      </p>
                    ))}
                  </div>

                  {info.title === "Téléphone" ? (
                    <a href="tel:+21697286007" className={`group/btn relative z-10 flex items-center gap-2 mx-auto bg-gradient-to-r ${info.color} text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}>
                      <span>{info.action}</span>
                      <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                    </a>
                  ) : info.title === "Email" ? (
                    <a href="mailto:placof.decor@gmail.com" className={`group/btn relative z-10 flex items-center gap-2 mx-auto bg-gradient-to-r ${info.color} text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}>
                      <span>{info.action}</span>
                      <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                    </a>
                  ) : (
                    <button className={`group/btn relative z-10 flex items-center gap-2 mx-auto bg-gradient-to-r ${info.color} text-white px-4 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}>
                      <span>{info.action}</span>
                      <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  )}

                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Formulaire de Contact et Carte */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-50/50 to-orange-50/50">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Formulaire */}
              <div
                id="contact-form"
                data-animate
                className={`${isVisible['contact-form'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  } transition-all duration-700`}
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-amber-100/50">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
                      <Send className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-black bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-2">
                      Envoyez-nous un Message
                    </h3>
                    <p className="text-gray-600">
                      Décrivez-nous votre projet, nous vous répondrons rapidement
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Nom complet <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Votre nom complet"
                              required
                              minLength={2}
                              maxLength={50}
                              className={`w-full pl-10 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all hover:border-amber-300 ${formErrors.name ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'
                                }`}
                            />
                          </div>
                          {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="votre.email@exemple.com"
                              required
                              className={`w-full pl-10 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all hover:border-amber-300 ${formErrors.email ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'
                                }`}
                            />
                          </div>
                          {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Téléphone <span className="text-red-500">*</span> <span className="text-gray-400 text-xs">(8 chiffres)</span>
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <div className="absolute left-10 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">+216</div>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="12 345 678"
                              pattern="[0-9]{8}"
                              maxLength={8}
                              className={`w-full pl-20 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all hover:border-amber-300 ${formErrors.phone ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'
                                }`}
                              required
                            />
                          </div>
                          {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Service <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10 pointer-events-none" />
                            <button
                              type="button"
                              className={`w-full pl-10 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all hover:border-amber-300 cursor-pointer font-semibold text-gray-700 flex items-center justify-between ${formErrors.service ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'}`}
                              onClick={() => setShowServicesDropdown((v) => !v)}
                              aria-haspopup="listbox"
                              aria-expanded={showServicesDropdown ? 'true' : 'false'}
                            >
                              {formData.service && services.find(s => s.value === formData.service) ? (
                                <span className="flex items-center gap-2">
                                  {(() => {
                                    const Icon = services.find(s => s.value === formData.service).icon;
                                    return <Icon className="w-5 h-5 text-amber-500" />;
                                  })()}
                                  <span className="font-bold text-amber-700">{services.find(s => s.value === formData.service).label}</span>
                                  <span className="hidden sm:inline text-xs text-gray-500 ml-2">{services.find(s => s.value === formData.service).desc}</span>
                                </span>
                              ) : (
                                <span className="text-gray-400">Sélectionnez un service</span>
                              )}
                              <ChevronLeft className={`w-5 h-5 text-gray-400 pointer-events-none transition-transform ${showServicesDropdown ? 'rotate-0' : 'rotate-[-90deg]'}`} />
                            </button>
                            {showServicesDropdown && (
                              <ul className="absolute left-0 right-0 mt-2 z-30 bg-white border border-amber-100 rounded-xl shadow-2xl max-h-72 overflow-y-auto animate-fade-in" role="listbox">
                                {services.map((service) => {
                                  const Icon = service.icon;
                                  return (
                                    <li
                                      key={service.value}
                                      role="option"
                                      aria-selected={formData.service === service.value}
                                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-amber-50 transition-all ${formData.service === service.value ? 'bg-amber-100' : ''}`}
                                      onClick={() => {
                                        setFormData({ ...formData, service: service.value });
                                        setShowServicesDropdown(false);
                                        if (formErrors.service) setFormErrors({ ...formErrors, service: '' });
                                      }}
                                    >
                                      <Icon className="w-6 h-6 text-amber-500 flex-shrink-0" />
                                      <div>
                                        <span className="font-bold text-amber-700">{service.label}</span>
                                        <span className="block text-xs text-gray-500">{service.desc}</span>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                          {formErrors.service && <p className="text-red-500 text-xs mt-1">{formErrors.service}</p>}
                        </div>
                      </div>

                      {formData.service === 'autre' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Précisez votre service <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Palette className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="text"
                              name="otherService"
                              value={formData.otherService}
                              onChange={handleInputChange}
                              placeholder="Décrivez le service souhaité..."
                              required={formData.service === 'autre'}
                              minLength={3}
                              maxLength={100}
                              className={`w-full pl-10 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all hover:border-amber-300 animate-fade-in ${formErrors.otherService ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'
                                }`}
                            />
                          </div>
                          {formErrors.otherService && <p className="text-red-500 text-xs mt-1">{formErrors.otherService}</p>}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Objet <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Star className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            placeholder="Objet de votre demande"
                            required
                            minLength={3}
                            maxLength={100}
                            className={`w-full pl-10 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all hover:border-amber-300 ${formErrors.subject ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'
                              }`}
                          />
                        </div>
                        {formErrors.subject && <p className="text-red-500 text-xs mt-1">{formErrors.subject}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Message <span className="text-red-500">*</span>
                          <span className="text-gray-400 text-xs ml-2">(1-1000 caractères)</span>
                        </label>
                        <div className="relative">
                          <MessageCircle className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Décrivez votre projet en détail : type d'espace, dimensions, style souhaité, budget approximatif, délai..."
                            required
                            minLength={10}
                            maxLength={1000}
                            rows={6}
                            className={`w-full pl-10 pr-4 py-3 bg-white/70 border-2 rounded-xl focus:ring-2 focus:ring-amber-500/30 transition-all resize-none hover:border-amber-300 ${formErrors.message ? 'border-red-500 focus:border-red-500' : 'border-amber-200/50 focus:border-amber-500'
                              }`}
                          />
                        </div>
                        {formErrors.message && <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>}
                        <p className="text-gray-400 text-xs mt-1">{formData.message.length}/1000</p>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative w-full flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-amber-500/25 transition-all duration-500 overflow-hidden transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin relative z-10" />
                          <span className="relative z-10">Envoi en cours...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                          <span className="relative z-10">Envoyer le Message</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Carte et informations */}
              <div
                id="contact-map"
                data-animate
                className={`space-y-8 ${isVisible['contact-map'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                  } transition-all duration-700 delay-200`}
              >
                {/* Carte simulée */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-amber-100/50">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-6">
                    Notre Localisation
                  </h3>
                  <div className="h-64 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10" />
                    <div className="text-center z-10">
                      <MapPin className="w-12 h-12 text-amber-600 mx-auto mb-2 animate-bounce" />
                      <p className="text-gray-500">Sfax, Tunisie</p>
                    </div>
                  </div>
                </div>

                {/* Réseaux sociaux */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-amber-100/50">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-6">
                    Suivez-nous
                  </h3>
                  <div className="flex gap-4">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        className={`group w-12 h-12 rounded-2xl bg-gradient-to-r ${social.color} flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 hover:rotate-12`}
                      >

                        {social.icon === 'tiktok' ? (
                          <svg className="w-6 h-6 text-white group-hover:animate-bounce" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                          </svg>
                        ) : (
                          <social.icon className="w-6 h-6 text-white group-hover:animate-bounce" />
                        )}
                      </a>
                    ))}
                  </div>
                  <p className="text-gray-600 mt-4">
                    Découvrez nos dernières réalisations et inspirations déco
                  </p>
                </div>

                {/* Pourquoi nous choisir */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-amber-100/50">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-6">
                    Pourquoi Nous Choisir ?
                  </h3>
                  <div className="space-y-4">
                    {whyChooseUs.map((reason, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                          <reason.icon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800">{reason.title}</h4>
                          <p className="text-gray-600 text-sm">{reason.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Témoignages */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Ce Que Disent Nos Clients
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Leur satisfaction est notre plus belle récompense
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  id={`testimonial-${index}`}
                  data-animate
                  className={`group relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-amber-100/50 transform hover:scale-105 ${isVisible[`testimonial-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  style={{
                    transitionDelay: `${index * 0.2}s`
                  }}
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-xl">
                    <Quote className="w-6 h-6 text-white" />
                  </div>

                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, starIndex) => (
                      <Star key={starIndex} className="w-5 h-5 text-amber-500 fill-current" />
                    ))}
                  </div>

                  <p className="text-gray-600 mb-6 italic">
                    "{testimonial.text}"
                  </p>

                  <div className="space-y-2">
                    <p className="text-sm text-amber-600 font-semibold">{testimonial.service}</p>
                  </div>

                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto text-center">
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-3xl p-12 shadow-2xl relative overflow-hidden">
              {/* Effet de brillance */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full animate-pulse" />

              <div className="relative z-10 space-y-8">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                    <Zap className="w-10 h-10 text-white animate-pulse" />
                  </div>
                </div>

                <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
                  Prêt à Transformer Votre Espace ?
                </h2>

                <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                  Contactez-nous dès maintenant pour une consultation gratuite et découvrez
                  comment nous pouvons donner vie à vos projets les plus ambitieux.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="group relative flex items-center gap-3 bg-white text-amber-600 px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-white/25 transition-all duration-500 overflow-hidden transform hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    <Calendar className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
                    <span className="relative z-10">Consultation Gratuite</span>
                  </button>

                  <a
                    href="tel:+21697286007"
                    className="group flex items-center gap-3 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/20 hover:border-white/40"
                  >
                    <Phone className="w-5 h-5 group-hover:animate-bounce" />
                    <span>+216 97 286 007</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactPage;
